import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { DialogWrapperComponent } from '../../wrappers/dialog-wrapper/dialog-wrapper.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ButtonComponent } from '../../button/button.component';
import { ButtonSize, ButtonStyle } from '../../../enums/button.enum';
import { BaseDialogDirective } from '../../base/base-dialog.directive';
import { DatePickerComponent } from '../../controllers/date-picker/date-picker.component';
import { TimePickerComponent } from '../../controllers/time-picker/time-picker.component';
import { TimeValidators } from '../../../validators/time.validators';
import {
  WooCommerceDatetimeSettings,
  WooCommerceDatetimeDeliveryDate,
  WooCommerceDatetimeTimeSlot
} from '../../../../core/interfaces/woo-commerce/api/woo-commerce-api.interface';
import { convertToDate, convertToString } from '../../../utils/date.utils';
import { getWorkingHoursForDate } from '../../../utils/business-hours.util';
import { filterOutHolidays } from '../../../utils/holidays.utils';
import { parseTimeRange } from '../../../utils/time.util';
import { DynamicValidationDependencies } from '../../controllers/control-error/validation-error-messages.token';
import {
  WooCommerceRestaurantHolidays,
  WooCommerceRestaurantHoursValue
} from '../../../../core/interfaces/woo-commerce/api/restaurant/restaurant-api.interface';
import { WooCommerceDatetimeTimeSlotValue } from '../../../../core/enums/woo-commerce/api/woo-commerce-api.enum';
import { CheckboxComponent } from '../../controllers/checkbox/checkbox.component';
import {
  DropdownComponent,
  DropdownSize,
  DropdownStyle
} from '../../controllers/dropdown/dropdown.component';
import { ToastrService } from 'ngx-toastr';
import { WooCommerceProductCategory } from '../../../../core/interfaces/woo-commerce/api/products/product-category-api.interface';
import { filterDatesByWeekdayAvailability } from '../../../utils/shipping-weekday-availability.util';

export interface DateTimeDialogData {
  date: Date;
  time: Date | string;
  timestamp: number;
}

export interface DateTimeDialogInputData {
  title?: string;
  datetimeSettings?: Pick<WooCommerceDatetimeSettings, 'delivery_dates' | 'weekdays'>;
  workingHours?: WooCommerceRestaurantHoursValue[];
  existingSchedule?: DateTimeDialogData;
  holidays?: WooCommerceRestaurantHolidays;
  timezone?: string;
  cartCategories?: WooCommerceProductCategory[];
}

@Component({
  selector: 'app-date-time-dialog',
  imports: [
    DialogWrapperComponent,
    ReactiveFormsModule,
    ButtonComponent,
    DatePickerComponent,
    TimePickerComponent,
    ...DynamicValidationDependencies,
    CheckboxComponent,
    DropdownComponent
  ],
  templateUrl: './date-time-dialog.component.html',
  styleUrl: './date-time-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateTimeDialogComponent
  extends BaseDialogDirective<DateTimeDialogData, DateTimeDialogInputData>
  implements OnInit, OnDestroy
{
  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly currentDate: Date = new Date();

  // Timer for periodic validation of selected date/time
  private validationIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly VALIDATION_INTERVAL_MS = 60000; // Check every minute

  protected availableDates: WritableSignal<Date[]> = signal([]);

  dateTimeForm!: FormGroup;
  isSlotMode: WritableSignal<boolean> = signal(false);
  timeOptions: WritableSignal<{ id: string; label: string }[]> = signal([]);
  showTimePicker: WritableSignal<boolean> = signal(true);
  timeLabel: WritableSignal<string> = signal('Time');
  protected readonly DropdownStyle = DropdownStyle;

  constructor(
    private formBuilder: FormBuilder,
    private toastrService: ToastrService
  ) {
    super();
  }

  ngOnInit() {
    this.initAvailableDates();
    this.initDateTimeForm();
    this.startValidationTimer();
  }

  ngOnDestroy() {
    this.stopValidationTimer();
  }

  /**
   * Initialize available dates by filtering past dates and holidays
   */
  private initAvailableDates(): void {
    const rawDates =
      this.dialogData.datetimeSettings?.delivery_dates?.map(
        (date: WooCommerceDatetimeDeliveryDate): Date => {
          return convertToDate(date.date);
        }
      ) || [];

    // Filter out holidays from available dates
    let filteredDates = filterOutHolidays(
      rawDates,
      this.dialogData.holidays,
      this.dialogData.timezone
    );

    // Filter out past dates (using timezone)
    const nowInTz = this.getNowInTimezone();
    filteredDates = this.filterPastDatesWithTimezone(filteredDates, nowInTz);
    filteredDates = filterDatesByWeekdayAvailability(
      filteredDates,
      this.dialogData.datetimeSettings?.weekdays,
      this.dialogData.timezone
    );

    if ((this.dialogData.workingHours || []).length > 0) {
      filteredDates = filteredDates.filter(
        (date) => getWorkingHoursForDate(date, this.dialogData.workingHours || []) != null
      );
    }

    this.availableDates.set(filteredDates);
  }

  /**
   * Start periodic validation to detect stale selections
   */
  private startValidationTimer(): void {
    this.validationIntervalId = setInterval(() => {
      this.validateCurrentSelection();
    }, this.VALIDATION_INTERVAL_MS);
  }

  private stopValidationTimer(): void {
    if (this.validationIntervalId) {
      clearInterval(this.validationIntervalId);
      this.validationIntervalId = null;
    }
  }

  /**
   * Validate the current selection and reset if it's now in the past
   */
  private validateCurrentSelection(): void {
    const formValues = this.dateTimeForm.getRawValue();
    const selectedDate = formValues['date'];
    const selectedTime = formValues['time'];
    const isAsap = formValues['is_asap'];

    // If ASAP is selected, no need to validate
    if (isAsap) {
      return;
    }

    // If no date selected, nothing to validate
    if (!selectedDate) {
      return;
    }

    const nowInTz = this.getNowInTimezone();

    // Check if the selected date is now in the past
    if (this.isDateInPastWithTimezone(selectedDate, nowInTz)) {
      // Reset the date and time selection
      this.dateTimeForm.patchValue({ date: null, time: null });
      this.dateTimeForm.get('time')?.disable();

      // Re-filter available dates
      this.initAvailableDates();

      // Notify user
      this.toastrService.warning(
        'The selected date has passed. Please choose a new date.',
        'Date Expired'
      );
      return;
    }

    // If the date is today, check if the time is still valid
    if (this.isSameDayWithTimezone(selectedDate, nowInTz) && selectedTime) {
      if (this.isTimeInPastWithTimezone(selectedTime, nowInTz)) {
        // Reset only the time selection
        this.dateTimeForm.patchValue({ time: null });

        // Re-trigger date change to update available time slots
        this.onDateSelect(selectedDate);

        // Notify user
        this.toastrService.warning(
          'The selected time has passed. Please choose a new time.',
          'Time Expired'
        );
      }
    }
  }

  onSubmit() {
    if (this.dateTimeForm.invalid) {
      this.dateTimeForm.markAllAsTouched();
      return;
    }

    const formValues = this.dateTimeForm.getRawValue();
    const selectedDate = formValues['date'];
    const selectedTime = formValues['time'];
    const isAsap = formValues['is_asap'];

    // Validate that the selected date/time is not in the past (unless ASAP)
    if (!isAsap) {
      const nowInTz = this.getNowInTimezone();

      // Check if date is in the past
      if (this.isDateInPastWithTimezone(selectedDate, nowInTz)) {
        // Reset form and refresh available dates
        this.dateTimeForm.patchValue({ date: null, time: null });
        this.initAvailableDates();
        this.toastrService.error(
          'The selected date has passed. Please choose a valid date.',
          'Invalid Date'
        );
        return;
      }

      // If today, check if time is in the past
      if (this.isSameDayWithTimezone(selectedDate, nowInTz) && selectedTime) {
        if (this.isTimeInPastWithTimezone(selectedTime, nowInTz)) {
          // Reset time and re-trigger date change to refresh time slots
          this.dateTimeForm.patchValue({ time: null });
          this.onDateSelect(selectedDate);
          this.toastrService.error(
            'The selected time has passed. Please choose a valid time.',
            'Invalid Time'
          );
          return;
        }
      }
    }

    const timeSlot = isAsap
      ? WooCommerceDatetimeTimeSlotValue.Asap
      : selectedTime;

    this.closeDialog({
      date: selectedDate,
      time: timeSlot,
      timestamp: formValues['timestamp']
    });
  }

  onDateSelect(date: Date) {
    const deliveryDate: WooCommerceDatetimeDeliveryDate =
      this.dialogData.datetimeSettings?.delivery_dates?.find(
        (deliveryDate: WooCommerceDatetimeDeliveryDate): boolean => {
          return deliveryDate?.date === convertToString(date);
        }
      )!;

    let rawTimeSlots: WooCommerceDatetimeTimeSlot[] =
      deliveryDate?.time_slots || [];

    // Filter out past time slots if the selected date is today (using timezone)
    const nowInTz = this.getNowInTimezone();
    if (this.isSameDayWithTimezone(date, nowInTz)) {
      rawTimeSlots = this.filterPastTimeSlotsWithTimezone(
        rawTimeSlots,
        nowInTz
      );
    }

    // Filter out 'asap' from slots for dropdown as it's handled by checkbox
    const timeSlots = rawTimeSlots.filter(
      (slot) =>
        (slot.time_slot as string).toLowerCase() !==
        WooCommerceDatetimeTimeSlotValue.Asap
    );

    // Check if only ASAP is available (0 slots or only asap in rawTimeSlots)
    const isOnlyAsapAvailable =
      timeSlots.length === 0 &&
      rawTimeSlots.some(
        (slot) =>
          (slot.time_slot as string).toLowerCase() ===
          WooCommerceDatetimeTimeSlotValue.Asap
      );

    // Check if no time slots at all (empty array)
    const hasNoTimeSlots = rawTimeSlots.length === 0;

    // Look up working hours for the selected date's weekday
    const workingHoursRange = getWorkingHoursForDate(
      date,
      this.dialogData.workingHours || []
    );
    const hasWorkingHours = !!workingHoursRange;

    // Preserve existing time selection if the same date is selected
    const existingSchedule = this.dialogData.existingSchedule;
    const isSameDate =
      existingSchedule &&
      convertToString(existingSchedule.date) === convertToString(date);

    let existingTime: Date | string | null = null;
    if (isSameDate && existingSchedule.time) {
      // Convert ISO date strings to Date objects for time picker
      if (existingSchedule.time instanceof Date) {
        existingTime = existingSchedule.time;
      } else if (typeof existingSchedule.time === 'string') {
        // Check if it's an ISO date string
        const isoDateMatch = existingSchedule.time.match(
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
        );
        if (isoDateMatch) {
          const dateObj = new Date(existingSchedule.time);
          if (!isNaN(dateObj.getTime())) {
            existingTime = dateObj;
          } else {
            existingTime = existingSchedule.time;
          }
        } else {
          // It's a time slot string, keep as string
          existingTime = existingSchedule.time;
        }
      } else {
        existingTime = existingSchedule.time;
      }
    }

    const existingIsAsap =
      isSameDate &&
      existingSchedule.time === WooCommerceDatetimeTimeSlotValue.Asap;

    if (timeSlots.length > 0) {
      // Slot Mode - has time slots (excluding asap)
      this.isSlotMode.set(true);
      this.showTimePicker.set(true);

      // Get the first and last slot times for the label
      const firstSlot = timeSlots[0].time_slot as string;
      const lastSlot = timeSlots[timeSlots.length - 1].time_slot as string;

      // Parse to show just the start of first and end of last for a cleaner label
      const firstTimeRange = parseTimeRange(firstSlot);
      const lastTimeRange = parseTimeRange(lastSlot);

      if (firstTimeRange && lastTimeRange) {
        this.timeLabel.set(
          `Time (${firstTimeRange.start.label} - ${lastTimeRange.end.label})`
        );
      } else {
        this.timeLabel.set(`Time (${firstSlot} - ${lastSlot})`);
      }

      this.timeOptions.set(
        timeSlots.map((slot) => ({
          id: slot.time_slot as string,
          label: slot.time_slot_i18n || (slot.time_slot as string)
        }))
      );

      // Preserve existing time if same date, otherwise reset
      if (existingTime && !existingIsAsap) {
        this.dateTimeForm.get('time')!.setValue(existingTime);
      } else {
        this.dateTimeForm.get('time')!.reset();
      }
      this.dateTimeForm.get('time')!.enable();
      this.dateTimeForm.get('time')!.clearValidators();
      this.dateTimeForm.get('time')!.setValidators([Validators.required]);
    } else if (hasWorkingHours) {
      // Range Mode - use per-weekday working hours from get_site_info
      // Show time picker with time range validations
      this.isSlotMode.set(false);
      this.showTimePicker.set(true);

      // Preserve existing time if same date, otherwise reset
      // For range mode, we need a Date object for the time picker
      if (existingTime && !existingIsAsap) {
        if (existingTime instanceof Date) {
          this.dateTimeForm.get('time')!.setValue(existingTime);
        } else if (typeof existingTime === 'string') {
          // Convert ISO date string to Date object
          const isoDateMatch = existingTime.match(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
          );
          if (isoDateMatch) {
            const dateObj = new Date(existingTime);
            if (!isNaN(dateObj.getTime())) {
              this.dateTimeForm.get('time')!.setValue(dateObj);
            } else {
              this.dateTimeForm.get('time')!.reset();
            }
          } else {
            // It's a time slot string, not valid for time picker in range mode
            this.dateTimeForm.get('time')!.reset();
          }
        } else {
          this.dateTimeForm.get('time')!.reset();
        }
      } else {
        this.dateTimeForm.get('time')!.reset();
      }
      this.dateTimeForm.get('time')!.enable();
      this.dateTimeForm.get('time')!.clearValidators();

      this.timeLabel.set(`Time (${workingHoursRange!.range})`);

      this.dateTimeForm
        .get('time')!
        .setValidators([
          Validators.required,
          TimeValidators.isTimeInRange([workingHoursRange!.range])
        ]);
    } else if (isOnlyAsapAvailable || hasNoTimeSlots) {
      // Only ASAP available or no time slots and no working hours
      // Hide time picker, enable and check ASAP
      this.isSlotMode.set(false);
      this.showTimePicker.set(false);
      this.dateTimeForm.get('time')!.reset();
      this.dateTimeForm.get('time')!.disable();
      this.dateTimeForm.get('time')!.clearValidators();
    } else {
      // Fallback - should not normally reach here
      this.isSlotMode.set(false);
      this.showTimePicker.set(true);
      this.dateTimeForm.get('time')!.reset();
      this.dateTimeForm.get('time')!.enable();
      this.dateTimeForm.get('time')!.clearValidators();
      this.dateTimeForm.get('time')!.setValidators([Validators.required]);
    }

    // Update TimeStamp
    this.dateTimeForm.get('timestamp')!.setValue(deliveryDate.timestamp);

    // Enable/Disable ASAP Field
    // ASAP is available if:
    // 1. Only ASAP is in time_slots, OR
    // 2. No time slots and no working hours (pure ASAP mode), OR
    // 3. ASAP is explicitly in the time_slots array
    const isAsapAvailable: boolean =
      isOnlyAsapAvailable ||
      (hasNoTimeSlots && !hasWorkingHours) ||
      rawTimeSlots.some((slot) => {
        return (
          (slot.time_slot as string).toLowerCase() ===
          WooCommerceDatetimeTimeSlotValue.Asap
        );
      });

    if (isAsapAvailable) {
      // Auto-check and disable ASAP if it's the only option:
      // - Only ASAP is available (and no working hours), OR
      // - No time slots and no working hours (pure ASAP mode)
      if ((isOnlyAsapAvailable || hasNoTimeSlots) && !hasWorkingHours) {
        this.dateTimeForm.get('is_asap')!.setValue(true);
        this.dateTimeForm.get('is_asap')!.setValidators([Validators.required]);
        this.dateTimeForm.get('is_asap')!.disable(); // Disable when it's the only option
      } else {
        // Enable ASAP checkbox when there are other options available
        // Preserve existing ASAP selection if same date
        this.dateTimeForm.get('is_asap')!.enable();
        if (existingIsAsap) {
          this.dateTimeForm.get('is_asap')!.setValue(true);
          this.onIsAsapChange(true);
        } else {
          this.dateTimeForm.get('is_asap')!.setValue(false);
        }
        this.dateTimeForm.get('is_asap')!.clearValidators();
      }
    } else {
      this.dateTimeForm.get('is_asap')!.disable();
      this.dateTimeForm.get('is_asap')!.setValue(false);
      this.dateTimeForm.get('is_asap')!.clearValidators();
    }

    this.dateTimeForm.updateValueAndValidity();
    this.dateTimeForm.get('time')!.updateValueAndValidity();
    this.dateTimeForm.get('is_asap')!.updateValueAndValidity();
  }

  onIsAsapChange(isAsap: boolean) {
    if (isAsap) {
      this.dateTimeForm.get('time')!.disable();
      this.dateTimeForm.get('time')!.removeValidators(Validators.required);
      this.dateTimeForm.get('time')!.reset();
    } else {
      this.dateTimeForm.get('time')!.enable();
      // Only add required validator if time picker should be shown
      if (this.showTimePicker()) {
        this.dateTimeForm.get('time')!.addValidators(Validators.required);
      }
    }

    this.dateTimeForm.get('time')!.updateValueAndValidity();
  }

  private initDateTimeForm() {
    const existingSchedule = this.dialogData.existingSchedule;

    // Initialize with existing values if available
    let initialDate = null;
    let initialTime = null;
    let initialIsAsap = false;
    let initialTimestamp = null;

    if (existingSchedule) {
      // Convert date string to Date object if needed
      if (existingSchedule.date instanceof Date) {
        initialDate = existingSchedule.date;
      } else if (typeof existingSchedule.date === 'string') {
        initialDate = new Date(existingSchedule.date);
        if (isNaN(initialDate.getTime())) {
          initialDate = null;
        }
      } else {
        initialDate = existingSchedule.date;
      }

      initialTimestamp = existingSchedule.timestamp;

      // Handle time - could be Date, string (time slot or ISO date string), or "asap"
      if (existingSchedule.time === WooCommerceDatetimeTimeSlotValue.Asap) {
        initialIsAsap = true;
      } else if (existingSchedule.time instanceof Date) {
        initialTime = existingSchedule.time;
      } else if (typeof existingSchedule.time === 'string') {
        // Check if it's an ISO date string (e.g., "1970-01-01T08:10:00.000Z")
        const isoDateMatch = existingSchedule.time.match(
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
        );
        if (isoDateMatch) {
          // Convert ISO date string to Date object for time picker
          const dateObj = new Date(existingSchedule.time);
          if (!isNaN(dateObj.getTime())) {
            initialTime = dateObj;
          } else {
            // Invalid date string, keep as string (might be a time slot)
            initialTime = existingSchedule.time;
          }
        } else {
          // It's a time slot string (e.g., "09:00 AM - 10:00 AM"), keep as string
          initialTime = existingSchedule.time;
        }
      }
    }

    this.dateTimeForm = this.formBuilder.group({
      date: [initialDate, [Validators.required]],
      time: initialTime,
      is_asap: initialIsAsap,
      timestamp: [initialTimestamp, [Validators.required]]
    });

    // If we have an existing schedule, trigger date selection to set up validators and UI
    if (initialDate) {
      // Use setTimeout to ensure form is initialized first
      setTimeout(() => {
        // onDateSelect will preserve the existing time if it's the same date
        // It will also set up the correct UI mode (slot vs range) and validators
        this.onDateSelect(initialDate!);
      }, 0);
    } else {
      this.dateTimeForm.get('time')!.disable();
      this.dateTimeForm.get('is_asap')!.disable();
    }

    this.dateTimeForm.get('timestamp')!.disable();
  }

  protected readonly DropdownSize = DropdownSize;

  /**
   * Get current date/time in the dialog's timezone
   * @returns Object with date components in the timezone
   */
  private getNowInTimezone(): {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
    totalMinutes: number;
  } {
    const now = new Date();
    const tz = this.dialogData.timezone;

    if (tz) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false
        });
        const parts = formatter.formatToParts(now);
        const year = parseInt(
          parts.find((p) => p.type === 'year')?.value || '0',
          10
        );
        const month =
          parseInt(parts.find((p) => p.type === 'month')?.value || '1', 10) - 1;
        const day = parseInt(
          parts.find((p) => p.type === 'day')?.value || '1',
          10
        );
        const hours = parseInt(
          parts.find((p) => p.type === 'hour')?.value || '0',
          10
        );
        const minutes = parseInt(
          parts.find((p) => p.type === 'minute')?.value || '0',
          10
        );

        return {
          year,
          month,
          day,
          hours,
          minutes,
          totalMinutes: hours * 60 + minutes
        };
      } catch (e) {
        console.warn('Failed to parse timezone, using local time:', e);
      }
    }

    // Fallback to local time
    return {
      year: now.getFullYear(),
      month: now.getMonth(),
      day: now.getDate(),
      hours: now.getHours(),
      minutes: now.getMinutes(),
      totalMinutes: now.getHours() * 60 + now.getMinutes()
    };
  }

  /**
   * Filter out dates that are in the past (timezone-aware)
   */
  private filterPastDatesWithTimezone(
    dates: Date[],
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): Date[] {
    return dates.filter((date: Date) => {
      const dateYear = date.getFullYear();
      const dateMonth = date.getMonth();
      const dateDay = date.getDate();

      // Compare as date only (ignore time)
      if (dateYear > nowInTz.year) return true;
      if (dateYear < nowInTz.year) return false;
      if (dateMonth > nowInTz.month) return true;
      if (dateMonth < nowInTz.month) return false;
      return dateDay >= nowInTz.day;
    });
  }

  /**
   * Filter out time slots that are in the past (timezone-aware)
   */
  private filterPastTimeSlotsWithTimezone(
    timeSlots: WooCommerceDatetimeTimeSlot[],
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): WooCommerceDatetimeTimeSlot[] {
    return timeSlots.filter((slot: WooCommerceDatetimeTimeSlot) => {
      const slotStr = slot.time_slot as string;

      // Always keep ASAP option
      if (slotStr.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap) {
        return true;
      }

      const timeRange = parseTimeRange(slotStr);
      if (!timeRange) {
        // If we can't parse the slot, keep it (might be a special format)
        return true;
      }

      // Keep the slot if its end time is still in the future
      const endMinutes = timeRange.end.milliseconds / (60 * 1000);
      return endMinutes > nowInTz.totalMinutes;
    });
  }

  /**
   * Check if a date is in the past (timezone-aware)
   */
  private isDateInPastWithTimezone(
    date: Date,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    const dateDay = date.getDate();

    if (dateYear < nowInTz.year) return true;
    if (dateYear > nowInTz.year) return false;
    if (dateMonth < nowInTz.month) return true;
    if (dateMonth > nowInTz.month) return false;
    return dateDay < nowInTz.day;
  }

  /**
   * Check if a date is the same day as the current date in timezone
   */
  private isSameDayWithTimezone(
    date: Date,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    return (
      date.getFullYear() === nowInTz.year &&
      date.getMonth() === nowInTz.month &&
      date.getDate() === nowInTz.day
    );
  }

  /**
   * Check if a time is in the past (timezone-aware)
   */
  private isTimeInPastWithTimezone(
    time: Date | string,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    if (time instanceof Date) {
      const timeMinutes = time.getHours() * 60 + time.getMinutes();
      return timeMinutes <= nowInTz.totalMinutes;
    }

    if (typeof time === 'string') {
      // Handle ASAP - never in the past
      if (time.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap) {
        return false;
      }

      // Parse time slot string
      const timeRange = parseTimeRange(time);
      if (timeRange) {
        // Check if the end time is in the past
        const endMinutes = timeRange.end.milliseconds / (60 * 1000);
        return endMinutes <= nowInTz.totalMinutes;
      }
    }

    return false;
  }
}
